'use client';

import * as React from 'react';
import { Input, Switch } from '@/components/ui';
import { cn } from '@/lib/utils';

interface MediaToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  statusFilter: 'all' | 'uploaded' | 'pending';
  onStatusFilterChange: (value: 'all' | 'uploaded' | 'pending') => void;
  aiVisible?: boolean;
  onAiVisibleChange?: (value: boolean) => void;
}

const STATUS_FILTERS: { value: 'all' | 'uploaded' | 'pending'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'pending', label: 'Pending' },
];

export function MediaToolbar({
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  aiVisible,
  onAiVisibleChange,
}: MediaToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-neutral-200 bg-white px-4 py-3 dark:border-neutral-700 dark:bg-neutral-900">
      <div className="min-w-[200px] flex-1">
        <Input
          type="search"
          aria-label="Search photos and videos"
          placeholder="Search photos and videos"
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>

      <div role="group" aria-label="Status filter" className="flex items-center gap-1">
        {STATUS_FILTERS.map((filter) => {
          const isActive = statusFilter === filter.value;
          return (
            <button
              key={filter.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => onStatusFilterChange(filter.value)}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-primary-500 text-white'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700'
              )}
            >
              {filter.label}
            </button>
          );
        })}
      </div>

      {onAiVisibleChange && (
        <Switch checked={!!aiVisible} onCheckedChange={onAiVisibleChange} aria-label="Show AI insights" label="AI insights" />
      )}
    </div>
  );
}
